import { useEffect, useMemo, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { toast } from "sonner"
import { PlaneIcon, HotelIcon, UsersIcon, CalendarIcon, X } from "lucide-react"
import { useBooking } from "@/context/BookingContext"
import { resolveAirportCode } from "@/lib/airports"
import {
  formatDateLabel,
  formatDateRange,
  formatDayLabel,
  getNightCount,
  getSelectedStayPeriod,
  getStayPeriodLabel,
} from "@/lib/booking"
import type { AirportOption, BookingRequestFormValues } from "@/types/booking"
import { SharedRoomModal } from "./SharedRoomModal"
import { AirportCombobox } from "./AirportCombobox"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

interface HeroSectionProps {
  onSearch: (overrides?: { departureAirports?: AirportOption[]; shared?: boolean }) => void
  onOpenBookingRequest: (
    initialValues?: Partial<BookingRequestFormValues>
  ) => void
}

const HERO_HIGHLIGHTS: { text: string; color: string; bg: string }[] = [
  { text: "Official travel packages", color: "#00ff88", bg: "rgba(0, 255, 136, 0.14)" },
  { text: "Hotels near the venue", color: "#00a8ff", bg: "rgba(0, 168, 255, 0.14)" },
  { text: "Secure online checkout", color: "#b366ff", bg: "rgba(179, 102, 255, 0.14)" },
]

const FLIGHT_ONLY_HIGHLIGHTS: { text: string; color: string; bg: string }[] = [
  { text: "Event-ready flight deals", color: "#00ff88", bg: "rgba(0, 255, 136, 0.14)" },
  { text: "Optional travel extras", color: "#00a8ff", bg: "rgba(0, 168, 255, 0.14)" },
  { text: "Secure online checkout", color: "#b366ff", bg: "rgba(179, 102, 255, 0.14)" },
]

export function HeroSection({
  onSearch,
  onOpenBookingRequest,
}: HeroSectionProps) {
  const { state, dispatch } = useBooking()
  const logoUrl = state.event?.logo ?? ""
  const [sharedRoomModalOpen, setSharedRoomModalOpen] = useState(false)
  const [mobileBenefitIndex, setMobileBenefitIndex] = useState(0)
  const [airportFieldError, setAirportFieldError] = useState(false)
  const [customDatesOpen, setCustomDatesOpen] = useState(false)
  const [departureCalendarOpen, setDepartureCalendarOpen] = useState(false)
  const [returnCalendarOpen, setReturnCalendarOpen] = useState(false)
  const initializedEventIdRef = useRef<string | null>(null)

  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  tomorrow.setHours(0, 0, 0, 0)

  useEffect(() => {
    if (state.customFlightDepartureDate || state.customFlightReturnDate) {
      setCustomDatesOpen(true)
    } else {
      setCustomDatesOpen(false)
    }
  }, [state.customFlightDepartureDate, state.customFlightReturnDate])

  const eventName = state.event?.name ?? "VCT EMEA Finals 2026"
  const eventLocation = state.event?.location ?? "Barcelona"
  const eventTitleLocation = eventLocation.split(",").at(-1)?.trim() || eventLocation
  const eventVenue = state.event?.venue
  const selectedStayPeriod = getSelectedStayPeriod(state)
  const stayPeriods = useMemo(() => {
    const periods = state.event?.stayPeriods ?? []
    return periods
      .map((period, originalIndex) => ({ period, originalIndex }))
      .sort((a, b) => {
        const labelA = getStayPeriodLabel(
          a.period.stayStartDate,
          a.period.stayEndDate,
          state.event?.startDate,
          state.event?.endDate,
          periods
        )
        const labelB = getStayPeriodLabel(
          b.period.stayStartDate,
          b.period.stayEndDate,
          state.event?.startDate,
          state.event?.endDate,
          periods
        )
        const priority: Record<string, number> = {
          "most-popular": 0,
          "full-event-stay": 1,
          "arrive-early": 2,
          "leave-late": 3,
          "flexible-dates": 4,
        }
        const pA = priority[labelA ?? ""] ?? 5
        const pB = priority[labelB ?? ""] ?? 5
        return pA - pB
      })
  }, [state.event])
  const hasMostPopular = stayPeriods.some(({ period }) => {
    const l = getStayPeriodLabel(period.stayStartDate, period.stayEndDate, state.event?.startDate, state.event?.endDate, state.event?.stayPeriods ?? [])
    return l === "most-popular"
  })
  const isSearchEnabled =
    state.eventStatus === "success" &&
    (state.selectedStayPeriodIndex !== null ||
      (state.packageType === "flight-only" &&
        !!state.customFlightDepartureDate &&
        !!state.customFlightReturnDate)) &&
    state.passengerCount < 5
  const isFlightOnlyMode = state.packageType === "flight-only"
  const customDepartureDate = state.customFlightDepartureDate ?? ""
  const customReturnDate = state.customFlightReturnDate ?? ""
  const heroHighlights = isFlightOnlyMode
    ? FLIGHT_ONLY_HIGHLIGHTS
    : HERO_HIGHLIGHTS
  const heroDescription = isFlightOnlyMode
    ? "Book your flights to the event, choose extra services, and complete everything online in minutes."
    : `Stay near ${eventVenue ?? eventLocation}, compare live package options, and complete your booking online.`

  useEffect(() => {
    const interval = window.setInterval(() => {
      setMobileBenefitIndex((current) => (current + 1) % heroHighlights.length)
    }, 2600)

    return () => window.clearInterval(interval)
  }, [heroHighlights.length])

  useEffect(() => {
    const eventId = state.event?.id
    const airportCode = state.event?.airport
    if (eventId && airportCode && initializedEventIdRef.current !== eventId) {
      let cancelled = false
      resolveAirportCode(airportCode).then((resolved) => {
        if (cancelled || !resolved) return
        initializedEventIdRef.current = eventId
        dispatch({ type: "SET_DESTINATION_AIRPORT", airport: resolved })
      })
      return () => { cancelled = true }
    }
  }, [state.event?.id, state.event?.airport, dispatch])

  const requestPrefill = useMemo<Partial<BookingRequestFormValues>>(
    () => ({
      departureAirport: state.departureAirports[0]?.code ?? "",
      destinationAirport: state.destinationAirport?.code ?? state.event?.airport ?? "",
      travelFrom: selectedStayPeriod?.stayStartDate ?? "",
      travelTo: selectedStayPeriod?.stayEndDate ?? "",
      travelers: state.passengerCount,
    }),
    [
      selectedStayPeriod?.stayEndDate,
      selectedStayPeriod?.stayStartDate,
      state.departureAirports,
      state.destinationAirport,
      state.event?.airport,
      state.passengerCount,
    ]
  )

  function handleAirportFallbackSearch(shared?: boolean) {
    if (
      state.packageType === "hotel-only" ||
      state.departureAirports.length > 0
    ) {
      onSearch({ shared })
      return
    }

    setAirportFieldError(true)
    toast.error("Please select a departure airport first.")
  }

  async function ensureDestinationAirport(): Promise<boolean> {
    if (state.destinationAirport) return true
    if (!state.event?.airport) return true

    const resolved = await resolveAirportCode(state.event.airport)
    if (resolved) {
      dispatch({ type: "SET_DESTINATION_AIRPORT", airport: resolved })
    }
    return true
  }

  async function handleExploreClick() {
    if (state.passengerCount >= 5) {
      onOpenBookingRequest(requestPrefill)
      return
    }

    if (
      (state.packageType === "flight-hotel" ||
        state.packageType === "flight-only") &&
      state.departureAirports.length === 0
    ) {
      handleAirportFallbackSearch()
      return
    }

    if (state.packageType !== "flight-only" && state.passengerCount === 1 && state.sharedRoom === null) {
      setSharedRoomModalOpen(true)
      return
    }

    await ensureDestinationAirport()
    onSearch()
  }

  const supportedModes = state.event?.supportedModes ?? []
  const showFlightHotel = supportedModes.includes("hotel_flight")
  const showHotelOnly = supportedModes.includes("hotel")
  const showFlightOnly = supportedModes.includes("flight")
  const showModeTabs = supportedModes.length > 1

  return (
    <section className="relative isolate overflow-hidden">
      <div className="brand-hero-gradient absolute inset-0 -z-10" />

      <div className="mx-auto flex min-h-[70vh] max-w-7xl flex-col items-center gap-10 px-4 py-10 @min-[64rem]:flex-row @min-[80rem]:gap-16 @min-[80rem]:py-16">
        <div className="order-2 w-full shrink-0 rounded-2xl bg-card/80 px-6 py-5 backdrop-blur-sm @min-[64rem]:order-1 @min-[64rem]:w-[400px]">
          {state.passengerCount < 5 && showModeTabs && (
            <Tabs
              value={state.packageType}
              onValueChange={(newValue) =>
                dispatch({
                  type: "SET_PACKAGE_TYPE",
                  packageType: newValue as "flight-hotel" | "hotel-only" | "flight-only",
                })
              }
              className="mb-6 h-auto gap-6"
            >
              <TabsList className="h-full! w-full">
                {showFlightHotel && (
                  <TabsTrigger
                    value="flight-hotel"
                    className="h-auto flex-col gap-0.5 py-1"
                  >
                    <div className="flex gap-2">
                      <PlaneIcon />
                      <HotelIcon />
                    </div>
                    Flight + Hotel
                  </TabsTrigger>
                )}
                {showHotelOnly && (
                  <TabsTrigger
                    value="hotel-only"
                    className="h-auto flex-col gap-0.5 py-1"
                  >
                    <HotelIcon />
                    Hotel Only
                  </TabsTrigger>
                )}
                {showFlightOnly && (
                  <TabsTrigger
                    value="flight-only"
                    className="h-auto flex-col gap-0.5 py-1"
                  >
                    <PlaneIcon />
                    Flight Only
                  </TabsTrigger>
                )}
              </TabsList>

              {(showFlightHotel || showFlightOnly) && (
                <TabsContent
                  value={state.packageType === "hotel-only" ? "flight-hotel" : state.packageType}
                >
                  <label className="mb-2 flex items-center gap-2 text-xs text-muted-foreground uppercase">
                    Flight from
                  </label>
                  <AirportCombobox
                    error={airportFieldError}
                    onErrorClear={() => setAirportFieldError(false)}
                  />
                  <label className="mt-3 mb-2 flex items-center gap-2 text-xs text-muted-foreground uppercase">
                    Flight to
                  </label>
                  <AirportCombobox mode="destination" />
                </TabsContent>
              )}
            </Tabs>
          )}
          {state.passengerCount < 5 && !showModeTabs && (showFlightHotel || showFlightOnly) && (
            <div className="mb-6">
              <label className="mb-2 flex items-center gap-2 text-xs text-muted-foreground uppercase">
                Flight from
              </label>
              <AirportCombobox
                error={airportFieldError}
                onErrorClear={() => setAirportFieldError(false)}
              />
              <label className="mt-3 mb-2 flex items-center gap-2 text-xs text-muted-foreground uppercase">
                Flight to
              </label>
              <AirportCombobox mode="destination" />
            </div>
          )}

          <div className={state.passengerCount >= 5 ? "" : "mb-6"}>
            <label className="mb-2 flex items-center gap-2 text-xs text-muted-foreground uppercase">
              Travelers
            </label>

            <div className="flex flex-col gap-2 @min-[40rem]:flex-row @min-[40rem]:items-center @min-[40rem]:gap-3">
              <div className="flex items-center gap-3">
                <UsersIcon />
                <span className="text-base text-foreground">
                  {state.passengerCount} adult
                  {state.passengerCount !== 1 ? "s" : ""}
                </span>
              </div>

              <div className="flex gap-1.5">
                {[1, 2, 3, 4].map((count) => (
                  <Button
                    key={count}
                    variant={
                      state.passengerCount === count ? "default" : "outline"
                    }
                    size="icon"
                    onClick={() =>
                      dispatch({ type: "SET_PASSENGER_COUNT", count })
                    }
                  >
                    {count}
                  </Button>
                ))}

                <Button
                  variant={state.passengerCount >= 5 ? "default" : "outline"}
                  size="icon"
                  onClick={() =>
                    dispatch({ type: "SET_PASSENGER_COUNT", count: 5 })
                  }
                >
                  5+
                </Button>
              </div>
            </div>

            {state.passengerCount >= 5 && (
              <div className="mt-4 flex flex-col gap-4 rounded-xl border border-primary/20 bg-primary/8 p-5">
                <div className="flex items-start gap-3">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-primary/15">
                    <UsersIcon className="size-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">
                      Group Booking (5+ travelers)
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                      We'll build a custom package for your group and confirm
                      availability with you personally.
                    </p>
                  </div>
                </div>
                <Button
                  className="w-full"
                  onClick={() => onOpenBookingRequest(requestPrefill)}
                >
                  Request a Group Package
                </Button>
              </div>
            )}
          </div>

          {state.passengerCount < 5 && (
            <>
              <div className="mb-6">
                <label className="mb-2 flex items-center gap-2 text-xs text-muted-foreground uppercase">
                  Dates
                </label>

                {state.eventStatus === "loading" ? (
                  <div className="rounded-xl border border-border bg-secondary/40 px-4 py-4 text-sm text-muted-foreground">
                    Loading available dates...
                  </div>
                ) : (
                  <div className="relative">
                    <div className={`grid grid-cols-2 items-end gap-2 transition-all duration-300 ${
                      isFlightOnlyMode && customDatesOpen ? "opacity-0 scale-95 pointer-events-none" : "opacity-100 scale-100"
                    }`}>
                      {stayPeriods.map(({ period, originalIndex }) => (
                        <StayPeriodCard
                          key={`${period.stayStartDate}-${period.stayEndDate}`}
                          stayPeriod={period}
                          selected={
                            state.selectedStayPeriodIndex === originalIndex
                          }
                          label={getStayPeriodLabel(
                            period.stayStartDate,
                            period.stayEndDate,
                            state.event?.startDate,
                            state.event?.endDate,
                            state.event?.stayPeriods ?? []
                          )}
                          hasMostPopular={hasMostPopular}
                          onSelect={() =>
                            dispatch({
                              type: "SET_STAY_PERIOD",
                              index: originalIndex,
                            })
                          }
                        />
                      ))}

                      {stayPeriods.length % 2 === 0 && <div />}

                      <Button
                        onClick={() => {
                          if (isFlightOnlyMode) {
                            setCustomDatesOpen(true)
                          } else {
                            onOpenBookingRequest(requestPrefill)
                          }
                        }}
                        variant="ghost"
                        className={`h-full flex-col overflow-hidden rounded-lg border-2 border-dashed bg-card p-0 text-left whitespace-normal transition-all hover:border-primary/50 ${
                          customDepartureDate || customReturnDate ? "border-primary/50 bg-primary/5" : "border-border"
                        }`}
                        type="button"
                      >
                        <div className="flex w-full flex-1 flex-col gap-2 p-4">
                          <p className="text-xs font-semibold text-primary uppercase">
                            CUSTOM DATES
                          </p>
                          {isFlightOnlyMode && (customDepartureDate || customReturnDate) ? (
                            <p className="text-sm font-medium text-foreground">
                              {customDepartureDate ? formatDateLabel(customDepartureDate) : "..."} → {customReturnDate ? formatDateLabel(customReturnDate) : "..."}
                            </p>
                          ) : (
                            <p className="text-sm font-medium text-foreground">
                              {isFlightOnlyMode
                                ? "Need different dates? Pick them."
                                : "Need different dates? Tell us."}
                            </p>
                          )}
                        </div>
                      </Button>
                    </div>

                    {isFlightOnlyMode && (
                      <div
                        className={`absolute inset-0 transition-all duration-300 ease-in-out ${
                          customDatesOpen
                            ? "opacity-100 scale-100"
                            : "opacity-0 scale-95 pointer-events-none"
                        }`}
                      >
                        <div className="rounded-xl border border-primary/20 bg-primary/5 p-4 h-full">
                          <div className="mb-3 flex items-center justify-between">
                            <span className="text-sm font-semibold text-foreground">
                              Custom Flight Dates
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => {
                                setCustomDatesOpen(false)
                                dispatch({ type: "CLEAR_CUSTOM_FLIGHT_DATES" })
                              }}
                              className="size-7 rounded-full text-muted-foreground hover:text-foreground"
                            >
                              <X className="size-4" />
                            </Button>
                          </div>

                          <div className="grid gap-3">
                            <div className="flex flex-col gap-1.5">
                              <label className="text-xs font-medium text-muted-foreground uppercase">
                                Departure
                              </label>
                              <Popover open={departureCalendarOpen} onOpenChange={setDepartureCalendarOpen}>
                                <PopoverTrigger
                                  className="inline-flex min-h-11 w-full items-center justify-start rounded-lg border border-input bg-input/30 px-4 text-left text-sm font-normal text-foreground hover:bg-input/60"
                                  type="button"
                                >
                                  <CalendarIcon className="mr-2 size-4 text-muted-foreground" />
                                  {customDepartureDate
                                    ? formatDateLabel(customDepartureDate)
                                    : "Pick a date"}
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-auto rounded-xl border-border bg-card p-0"
                                  align="start"
                                >
                                  <Calendar
                                    mode="single"
                                    selected={parseDate(customDepartureDate)}
                                    defaultMonth={parseDate(customDepartureDate) ?? tomorrow}
                                    onSelect={(date) => {
                                      if (date) {
                                        const departure = formatDateISO(date)
                                        setDepartureCalendarOpen(false)
                                        if (customReturnDate && customReturnDate < departure) {
                                          dispatch({
                                            type: "SET_CUSTOM_FLIGHT_DATES",
                                            departureDate: departure,
                                            returnDate: "",
                                          })
                                        } else {
                                          dispatch({
                                            type: "SET_CUSTOM_FLIGHT_DATES",
                                            departureDate: departure,
                                            returnDate: customReturnDate,
                                          })
                                        }
                                      }
                                    }}
                                    disabled={(date) => date < tomorrow}
                                    initialFocus
                                  />
                                </PopoverContent>
                              </Popover>
                            </div>

                            <div className="flex flex-col gap-1.5">
                              <label className="text-xs font-medium text-muted-foreground uppercase">
                                Return
                              </label>
                              <Popover open={returnCalendarOpen} onOpenChange={setReturnCalendarOpen}>
                                <PopoverTrigger
                                  className="inline-flex min-h-11 w-full items-center justify-start rounded-lg border border-input bg-input/30 px-4 text-left text-sm font-normal text-foreground hover:bg-input/60"
                                  type="button"
                                >
                                  <CalendarIcon className="mr-2 size-4 text-muted-foreground" />
                                  {customReturnDate
                                    ? formatDateLabel(customReturnDate)
                                    : "Pick a date"}
                                </PopoverTrigger>
                                <PopoverContent
                                  className="w-auto rounded-xl border-border bg-card p-0"
                                  align="start"
                                >
                                  <Calendar
                                    mode="single"
                                    selected={parseDate(customReturnDate)}
                                    defaultMonth={parseDate(customReturnDate) ?? parseDate(customDepartureDate)}
                                    onSelect={(date) => {
                                      if (date) {
                                        dispatch({
                                          type: "SET_CUSTOM_FLIGHT_DATES",
                                          departureDate: customDepartureDate,
                                          returnDate: formatDateISO(date),
                                        })
                                        setReturnCalendarOpen(false)
                                      }
                                    }}
                                    disabled={(date) =>
                                      customDepartureDate
                                        ? date < new Date(customDepartureDate)
                                        : date < tomorrow
                                    }
                                    initialFocus
                                  />
                                </PopoverContent>
                              </Popover>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {(state.eventError || state.offersError) && (
                <div className="mb-4 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                  {state.eventError?.userMessage ?? state.offersError?.userMessage}
                </div>
              )}

              <Button
                className="w-full"
                disabled={!isSearchEnabled}
                size="xl"
                onClick={handleExploreClick}
              >
                Explore Travel Packages
              </Button>
            </>
          )}
        </div>

        <div className="order-1 flex min-w-0 flex-1 items-center justify-center @min-[64rem]:order-2">
          <div className="flex flex-col items-center justify-center gap-3 py-4 text-center @min-[40rem]:gap-4 @min-[40rem]:py-6 @min-[64rem]:gap-6 @min-[64rem]:py-12">
            <div className="flex items-center justify-center">
              <img
                src={logoUrl}
                alt={`${eventName} travel packages logo`}
                className="h-14 w-auto object-contain @min-[40rem]:h-16 @min-[64rem]:h-24"
              />
            </div>

            <h1 className="text-center text-[32px] font-bold tracking-wide text-foreground uppercase @min-[40rem]:text-4xl @min-[64rem]:text-5xl @min-[80rem]:text-6xl">
              Travel to {eventName} - {eventTitleLocation}
            </h1>

            <p className="text-center text-base font-semibold tracking-wide text-foreground/80 uppercase @min-[40rem]:text-lg @min-[64rem]:text-xl">
              {formatDateRange(state.event?.startDate, state.event?.endDate)}
            </p>

            <p className="max-w-2xl text-center text-[15px] text-foreground/80 @min-[40rem]:text-base @min-[64rem]:text-lg @min-[80rem]:text-xl">
              {heroDescription}
            </p>

            <div className="mt-1 hidden flex-col gap-5 @min-[64rem]:flex">
              {heroHighlights.map((highlight) => (
                <HighlightRow
                  key={highlight.text}
                  text={highlight.text}
                  color={highlight.color}
                  bg={highlight.bg}
                />
              ))}
            </div>

            <div className="mt-1 flex min-h-10 items-center @min-[64rem]:hidden">
              <div
                key={heroHighlights[mobileBenefitIndex].text}
                className="animate-benefit-cycle flex items-center gap-2.5"
              >
                <HighlightRow
                  text={heroHighlights[mobileBenefitIndex].text}
                  color={heroHighlights[mobileBenefitIndex].color}
                  bg={heroHighlights[mobileBenefitIndex].bg}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {sharedRoomModalOpen && state.packageType !== "flight-only" && (
        <SharedRoomModal
          onChoice={(sharedRoom) => {
            dispatch({
              type: "SET_ROOM_PREFERENCE",
              sharedRoom,
              roomCapacity: sharedRoom ? 2 : 1,
            })
            setSharedRoomModalOpen(false)
            handleAirportFallbackSearch(sharedRoom)
          }}
          onCancel={() => setSharedRoomModalOpen(false)}
        />
      )}
    </section>
  )
}


function parseDate(value: string): Date | undefined {
  if (!value) return undefined
  const d = new Date(value)
  return isNaN(d.getTime()) ? undefined : d
}

function formatDateISO(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, "0")
  const d = String(date.getDate()).padStart(2, "0")
  return `${y}-${m}-${d}`
}


function HighlightRow({ text, color, bg }: { text: string; color: string; bg: string }) {
  return (
    <div className="flex items-center gap-2.5" style={{ color }}>
      <div
        className="flex size-6 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: bg }}
      >
        <span className="text-sm">✓</span>
      </div>
      <span className="text-base">{text}</span>
    </div>
  )
}

function StayPeriodCard({
  stayPeriod,
  selected,
  label,
  hasMostPopular,
  onSelect,
}: {
  stayPeriod: {
    stayStartDate: string
    stayEndDate: string
  }
  selected: boolean
  label: "most-popular" | "full-event-stay" | "arrive-early" | "leave-late" | "flexible-dates" | null
  hasMostPopular: boolean
  onSelect: () => void
}) {
  const nights = getNightCount(stayPeriod.stayStartDate, stayPeriod.stayEndDate)

  const showBanner = label === "most-popular" || (label === "full-event-stay" && !hasMostPopular)
  const bannerText = "★ MOST POPULAR"
  const subtitleText = label === "most-popular"
    ? "EVENT DATES"
    : label === "full-event-stay" && !hasMostPopular
      ? "Full Event Stay"
      : label === "full-event-stay"
        ? "Full Event Stay"
        : label === "arrive-early"
          ? "Arrive Early"
          : label === "leave-late"
            ? "Leave Late"
            : label === "flexible-dates"
              ? "Flexible Dates"
              : null

  return (
    <Button
      onClick={onSelect}
      variant="ghost"
      className={`relative h-auto flex-col overflow-hidden rounded-lg bg-card p-0 text-left transition-all gap-0 ${
        selected ? "border-2 border-primary" : "border-2 border-border"
      }`}
      type="button"
    >
      {showBanner && (
        <div className="w-full py-1.5 text-center text-[10px] font-bold tracking-wider uppercase bg-primary text-primary-foreground">
          {bannerText}
        </div>
      )}
      <div className="w-full flex flex-1 flex-col items-start justify-end gap-2 p-4">
        {subtitleText && (
          <p className={`text-xs font-semibold uppercase ${
            label === "full-event-stay" && !hasMostPopular ? "text-primary" : label === "full-event-stay" ? "text-blue-500" : label === "flexible-dates" ? "text-foreground/60" : "text-primary"
          }`}>
            {subtitleText}
          </p>
        )}
        {!subtitleText && <div className="h-[18px]" />}

        <div className="flex items-center gap-2">
          <div className="flex flex-col items-start gap-0.5">
            <p className="text-[10px] text-foreground/80 uppercase">
              {formatDayLabel(stayPeriod.stayStartDate)}
            </p>
            <p className="text-sm font-bold text-foreground">
              {formatDateLabel(stayPeriod.stayStartDate)}
            </p>
          </div>

          <span className="pb-1 text-sm font-semibold text-foreground/70">
            –
          </span>

          <div className="flex flex-col items-start gap-0.5">
            <p className="text-[10px] text-foreground/80 uppercase">
              {formatDayLabel(stayPeriod.stayEndDate)}
            </p>
            <p className="text-sm font-bold text-foreground">
              {formatDateLabel(stayPeriod.stayEndDate)}
            </p>
          </div>
        </div>

        <p className="text-xs text-foreground/80 uppercase">
          {nights} NIGHTS
        </p>
      </div>
    </Button>
  )
}
